import client from './client';
import { gql } from '@apollo/client';
import { get, patch, post, del } from '../../helpers/api';

const getUsers = async ({ offset = 0, limit = 10 } = {}) => {
  const data = await client.query(
    gql`
      query users($offset: Int!, $limit: Int!) {
        users(offset: $offset, limit: $limit) {
          data {
            id
            firstName
            lastName
            email
            phoneNumber
            driver_id
            isCustomDriver
            driverType
          }
          count
        }
      }
    `,
    { offset, limit },
  );
  return data.users;
};

// `homeCity` selects all three fields the UI reads: cityName/stateAbbr render in the
// Identity card and the page header, timeZone formats timed driver blocks. Selecting only
// timeZone left the other two undefined, so a driver WITH a home city displayed "—" — and
// both render sites, seeing a truthy object with empty fields, grew a defensive filter for
// a symptom whose cause was this selection. Add fields here before rendering them.
const getUser = async ({ id }) => {
  const data = await client.query(
    gql`
      query user($id: String!) {
        user(id: $id) {
          id
          firstName
          middleName
          lastName
          email
          phoneNumber
          imageUrl
          driver_id
          rider_id
          customer_id
          driverApplicationStatus
          isCustomDriver
          driverType
          creditAmount
          referralCredit
          cancellationCredit
          cancellationReferralCredit
          blacklistedDates
          blacklistedTimes
          homeCity {
            cityName
            stateAbbr
            timeZone
          }
        }
        userRawUnsafe(id: $id) {
          jsonString
        }
      }
    `,
    { id },
  );
  data.user.userRawUnsafe = data.userRawUnsafe;
  return data.user;
};

const editUser = async ({ id, update }) => {
  const data = await client.query(
    gql`
      mutation updateUser($id: String!, $update: UserUpdateInput!) {
        updateUser(id: $id, input: $update) {
          id
          firstName
          middleName
          lastName
          email
          phoneNumber
          imageUrl
          driver_id
          rider_id
          customer_id
          driverApplicationStatus
          isCustomDriver
          driverType
          creditAmount
          referralCredit
          cancellationCredit
          cancellationReferralCredit
        }
      }
    `,
    { id, update },
  );
  return data.updateUser;
};

const getProfilePhotoUploadUrl = async ({ id }) => {
  // Presigned S3 POST for the profile photo. Returns
  // { uploadUrl, fields, imageUrl, requiresLiveCapture }: POST the file (with
  // `fields`) to `uploadUrl`, then pass `imageUrl` to commitProfilePhoto.
  // `requiresLiveCapture` is true for drivers (photo is face-matched on commit).
  const response = await get(`/admin/users/${id}/profile-photo/upload-url`);
  return response.data;
};

// Finalizes an uploaded profile photo. Returns { result, imageUrl }: result is
// one of "set" | "failed_match" | "no_face_detected" (all HTTP 200); imageUrl is
// the final public display URL, present only when result === "set". Throws on the
// ownership/validation 400. `bypassVerification` is the admin override for drivers
// whose photo fails the automated face-match; `bypassReason` is the admin's
// free-text justification, logged server-side with their admin id. It is omitted
// from the body when the UI didn't collect one.
const commitProfilePhoto = async ({ id, imageUrl, bypassVerification = false, bypassReason }) => {
  const response = await post(`/admin/users/${id}/profile-photo`, {
    imageUrl,
    bypassVerification,
    ...(bypassReason ? { bypassReason } : {}),
  });
  return response.data;
};

const getRiderCashFlow = async ({ id }) => {
  const data = await client.query(
    gql`
      query riderCashFlow($id: String!) {
        riderCashFlow(id: $id) {
          totalSeats
          totalRides
          totalTipped
          volume
        }
      }
    `,
    { id },
  );
  return data.riderCashFlow;
};

const getDriverCashFlow = async ({ id }) => {
  const data = await client.query(
    gql`
      query driverCashFlow($id: String!) {
        driverCashFlow(id: $id) {
          totalEarnings
          totalTrips
          totalRides
          totalTips
        }
      }
    `,
    { id },
  );
  return data.driverCashFlow;
};

const getPaymentSources = async ({ id }) => {
  const data = await client.query(
    gql`
      query paymentSources($id: String!) {
        paymentSources(id: $id) {
          id
          object
          isDefault
          brand
          last4
          exp_month
          exp_year
        }
      }
    `,
    { id },
  );
  return data.paymentSources;
};

const removePaymentSource = async ({ id, sourceId }) => {
  const data = await client.query(
    gql`
      mutation removePaymentSource($id: String!, $sourceId: String!) {
        removePaymentSource(id: $id, sourceId: $sourceId) {
          id
          object
          isDefault
          brand
          last4
          exp_month
          exp_year
        }
      }
    `,
    { id, sourceId },
  );
  return data.removePaymentSource;
};

const setDefaultPaymentSource = async ({ id, sourceId }) => {
  const data = await client.query(
    gql`
      mutation setDefaultPaymentSource($id: String!, $sourceId: String!) {
        setDefaultPaymentSource(id: $id, sourceId: $sourceId) {
          id
          object
          isDefault
          brand
          last4
          exp_month
          exp_year
        }
      }
    `,
    { id, sourceId },
  );
  return data.setDefaultPaymentSource;
};

const getSessions = async ({ id }) => {
  const data = await client.query(
    gql`
      query sessions($id: String!) {
        sessions(id: $id) {
          id
          startedAt
          lastAccessedAt
          expiresAt
          attributes {
            ipAddress
            userAgent
          }
        }
      }
    `,
    { id },
  );
  return data.sessions;
};

const revokeSession = async ({ id, sessionId }) => {
  const data = await client.query(
    gql`
      mutation revokeSession($id: String!, $sessionId: String!) {
        revokeSession(id: $id, sessionId: $sessionId) {
          id
          startedAt
          lastAccessedAt
          expiresAt
          attributes {
            ipAddress
            userAgent
          }
        }
      }
    `,
    { id, sessionId },
  );
  return data.revokeSession;
};

const getAverageDriverScores = async () => {
  const data = await client.query(gql`
    query averageDriverRatings {
      averageDriverRatings {
        id
        npsScore
        conversationScore
        cleanlinessScore
        drivingQualityScore
        carQualityScore
        totalScore
        percentile
      }
    }
  `);
  return data.averageDriverRatings;
};

const getDriverScore = async ({ id }) => {
  const data = await client.query(
    gql`
      query driverRatings($id: String!) {
        driverRatings(id: $id) {
          id
          npsScore
          conversationScore
          cleanlinessScore
          drivingQualityScore
          carQualityScore
          totalScore
          percentile
        }
      }
    `,
    { id },
  );
  return data.driverRatings;
};

/**
 * Fields only some backends have: `timeZone` from hitch-services#3644 onward, and
 * `refundPolicy` from the shared-non-refundable admin change.
 *
 * GraphQL rejects an unknown field at *validation* time, so selecting it against an
 * older backend fails the whole query rather than returning the field as null — the
 * rider's history would show its load-error state instead of rows missing one detail.
 * That is not hypothetical ordering paranoia: the environment switcher points one
 * build at staging or production, and the admin backend's prod deploy is manual, so
 * a single deploy can face both schemas.
 */
const OPTIONAL_RIDE_HISTORY_FIELDS = ['timeZone', 'refundPolicy'];

const rideHistoryQuery = (fields) => {
  const station = `
    stationName
    ${fields.has('timeZone') ? 'timeZone' : ''}
    city {
      cityName
    }
  `;
  const refundPolicy = fields.has('refundPolicy')
    ? `
      refundPolicy {
        kind
        refundable
        title
        details
      }
    `
    : '';

  return gql`
      query rideHistory($userId: String!, $offset: Int!, $limit: Int!) {
        rideHistory(userId: $userId, offset: $offset, limit: $limit) {
          count
          completed
          data {
            rideId
            tripId
            status
            departureTime
            product
            isStandby
            standbyStatus
            didRefundCash
            ${refundPolicy}
            fare {
              finalAmount
            }
            originStation {
              ${station}
            }
            destinationStation {
              ${station}
            }
            pickUp {
              name
              address
            }
            dropOff {
              name
              address
            }
          }
        }
      }
    `;
};

// Only GraphQL's unknown-field validation failure, naming one of the optional fields
// still being asked for, is droppable. Any other error that mentions the field (a
// resolver failure, a null in a non-null field) is real and must reach the caller
// rather than be swallowed behind a retry.
const unknownOptionalField = (error, fields) =>
  [...fields].find((field) =>
    (error?.graphQLErrors ?? []).some((e) => (e?.message ?? '').startsWith(`Cannot query field "${field}"`)),
  );

const getRideHistory = async ({ id: userId, offset = 0, limit = 25 }) => {
  const variables = { userId, offset, limit };
  const fields = new Set(OPTIONAL_RIDE_HISTORY_FIELDS);

  // Each retry drops one field, so this ends after at most one retry per field.
  for (;;) {
    try {
      const data = await client.query(rideHistoryQuery(fields), variables);
      return data.rideHistory;
    } catch (error) {
      const missing = unknownOptionalField(error, fields);
      if (!missing) {
        throw error;
      }
      // An older backend. Rows render without that detail (dates on the UTC
      // fallback, no refund policy) rather than not at all.
      fields.delete(missing);
    }
  }
};

const refundRide = async ({ rideId, amount, reason, description }) => {
  const data = await client.query(
    gql`
      mutation refundRide($rideId: String!, $amount: Float!, $reason: String!, $description: String) {
        refundRide(rideId: $rideId, amount: $amount, reason: $reason, description: $description) {
          id
          didRefundCash
        }
      }
    `,
    { rideId, amount, reason, description },
  );
  return data.refundRide;
};

const getDriveHistory = async ({ id, offset = 0, limit = 25 }) => {
  const data = await client.query(
    gql`
      query driveHistory($id: String!, $offset: Int!, $limit: Int!) {
        driveHistory(id: $id, offset: $offset, limit: $limit) {
          count
          data {
            _id
            status
            departureTime
            payout
            originCity {
              cityName
            }
            destinationCity {
              cityName
            }
          }
        }
      }
    `,
    { id, offset, limit },
  );
  return data.driveHistory;
};

const getPromotions = async ({ id }) => {
  const data = await client.query(
    gql`
      query promotions($id: String!) {
        promotions(id: $id) {
          id
          code
          usesLeft
          addedOn
          description
          subtitle
          type
          kind
          beforeEventCount
          afterEventCount
          startsOn
          endsOn
          startOn
          endOn
          maxUses
          minimumPurchaseAmount
          maximumDiscountAmount
          isReferral
          referralCredit
          personType
          shouldAutoApplyToNewUsers
          isActive
          options {
            id
            type
            amount
            priceHike
            routeType
            seatDiscounts
          }
        }
      }
    `,
    { id },
  );
  return data.promotions;
};

// Referrals moved to the admin REST API (the admin service is migrating off
// GraphQL). Returns the same shape the card expects: an array of sent
// referrals with recipient, status, redeemed, medium, and incentives.
const getReferrals = async ({ id }) => {
  const response = await get(`/admin/users/${id}/referrals`);
  return response.data;
};

const removePromo = async ({ id, promoId }) => {
  await client.query(
    gql`
      mutation removePromo($id: String!, $promoId: String!) {
        removePromo(id: $id, promoId: $promoId)
      }
    `,
    { id, promoId },
  );
};

const addPromos = async ({ id, codes }) => {
  await client.query(
    gql`
      mutation addPromos($id: String!, $codes: [String!]!) {
        addPromos(id: $id, codes: $codes)
      }
    `,
    { id, codes },
  );
};

const addBlacklistedDate = async ({ id, date }) => {
  await client.query(
    gql`
      mutation addBlacklistedDate($id: String!, $date: String!) {
        addBlacklistedDate(id: $id, date: $date)
      }
    `,
    { id, date },
  );
};

const removeBlacklistedDate = async ({ id, date }) => {
  await client.query(
    gql`
      mutation removeBlacklistedDate($id: String!, $date: String!) {
        removeBlacklistedDate(id: $id, date: $date)
      }
    `,
    { id, date },
  );
};

const addBlacklistedDateTime = async ({ id, dateTime }) => {
  await client.query(
    gql`
      mutation addBlacklistedDateTime($id: String!, $dateTime: String!) {
        addBlacklistedDateTime(id: $id, dateTime: $dateTime)
      }
    `,
    { id, dateTime },
  );
};

const removeBlacklistedDateTime = async ({ id, dateTime }) => {
  await client.query(
    gql`
      mutation removeBlacklistedDateTime($id: String!, $dateTime: String!) {
        removeBlacklistedDateTime(id: $id, dateTime: $dateTime)
      }
    `,
    { id, dateTime },
  );
};

const getCurrentLocation = async (userId) => {
  try {
    const response = await get(`/admin/location/current/${userId}`);
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getCreditLog = async ({ id, offset = 0, limit = 25 }) => {
  try {
    const response = await get(`/admin/credits/${id}/log?offset=${offset}&limit=${limit}`);
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const addCredits = async ({ userId, reason, description, items, rideId }) => {
  try {
    const response = await post(`/admin/credits/${userId}`, {
      reason,
      description,
      items,
      rideId,
    });
    return response;
  } catch (err) {
    throw err;
  }
};

export const getUserNotes = async ({ id }) => {
  try {
    const response = await get(`/admin/users/${id}/notes`);
    return response.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Both directions of a person's reviews in one call — `{ asDriver, asRider }`.
 *
 * The id is the Mongo USER id, which is what `rated_user` holds on every rating row.
 * Passing a driver_id or rider_id returns empty lists rather than an error, so a wrong
 * id here looks exactly like a person nobody has rated.
 *
 * One request for both halves because `rated_user` keys both; the Rider and Driver tabs
 * each read their own half of the same response.
 */
export const getUserReviews = async ({ id, limit = 25 }) => {
  const response = await get(`/admin/users/${id}/reviews?limit=${limit}`);
  return response.data;
};

/**
 * Every install this person is signed in on — `{ rows, returned, pushable }`.
 *
 * Reads the Postgres `device` table — one row per install, keyed on the client's device
 * id. `pushable` is the count that can actually receive a push, which needs both a token
 * on file and the OS permission granted. The token itself is deliberately not served.
 */
export const getUserDevices = async ({ id }) => {
  const response = await get(`/admin/users/${id}/devices`);
  return response.data;
};

/**
 * This person's Crisp contact — `{ peopleId, sessionIds }`.
 *
 * The admin service looks Crisp up by the `user_id` the apps tag on every Crisp contact
 * and conversation, so the id is the Mongo USER id. `peopleId` is null and `sessionIds`
 * empty when Crisp has nothing for them; `sessionIds` are open conversations only.
 */
export const getUserCrispContact = async ({ id }) => {
  const response = await get(`/admin/users/${id}/crisp`);
  return response.data;
};

export const addUserNote = async ({ userId, body }) => {
  try {
    const response = await post(`/admin/users/${userId}/notes`, { body });
    return response;
  } catch (err) {
    throw err;
  }
};

export const editUserNote = async ({ userId, noteId, body }) => {
  const response = await patch(`/admin/users/${userId}/notes/${noteId}`, { body });
  return response;
};

const getDriverBalance = async ({ id }) => {
  const response = await get(`/admin/driver-credits/${id}/balance`);
  return response.data;
};

const getDriverPaymentLog = async ({ id, cursor, limit = 25 }) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) {
    params.set('cursor', cursor);
  }
  const response = await get(`/admin/driver-credits/${id}/log?${params.toString()}`);
  return response.data;
};

const addDriverPayment = async ({ userId, amount, reason, description }) => {
  const response = await post(`/admin/driver-credits/${userId}`, {
    amount,
    reason,
    description,
  });
  return response;
};

const deleteUser = async ({ id }) => {
  await del(`/admin/users/${id}`);
};

const reverseDriverPayment = async ({ userId, auditLogId, description }) => {
  const response = await post(`/admin/driver-credits/${userId}/reverse`, {
    auditLogId,
    description,
  });
  return response;
};

const UsersApi = {
  getUsers,
  getUser,
  editUser,
  getProfilePhotoUploadUrl,
  commitProfilePhoto,
  getRiderCashFlow,
  getDriverCashFlow,
  getPaymentSources,
  removePaymentSource,
  setDefaultPaymentSource,
  getSessions,
  revokeSession,
  getDriverScore,
  getAverageDriverScores,
  getRideHistory,
  refundRide,
  getDriveHistory,
  getPromotions,
  getReferrals,
  removePromo,
  addPromos,
  addBlacklistedDate,
  removeBlacklistedDate,
  addBlacklistedDateTime,
  removeBlacklistedDateTime,
  getCurrentLocation,
  getCreditLog,
  addCredits,
  getUserNotes,
  getUserReviews,
  getUserDevices,
  addUserNote,
  editUserNote,
  getDriverBalance,
  getDriverPaymentLog,
  addDriverPayment,
  reverseDriverPayment,
  deleteUser,
};

export default UsersApi;
